import { useCallback, useState } from 'react';
import type { GameMeta } from '@photo-merge/shared';
import { exportSizeFor, renderBall, toUploadBlob } from './export';
import type { Photo } from './photos';
import { uploadAll } from './upload';
import type { SaveProgress } from './useSaveGame';
import { initEdit, saveEdit } from '../lib/api';

/**
 * Saves an edited roster.
 *
 * Only the photos added in this session are uploaded. A photo that was already in the
 * game keeps the image it's already stored under, even if it moved rank — which makes
 * a reorder or a removal cost no bytes at all, and means the balls other people are
 * mid-game with never change under them.
 */
export function useSaveEdit() {
  const [progress, setProgress] = useState<SaveProgress>({ phase: 'idle', fraction: 0 });

  const save = useCallback(
    async (slug: string, passphrase: string, photos: Photo[]): Promise<GameMeta | null> => {
      try {
        const added = photos.filter((photo) => !photo.savedKey);

        setProgress({ phase: 'preparing', fraction: 0.05, message: 'Checking your edits…' });
        const { uploads } = await initEdit(slug, passphrase, added.length);

        if (added.length) {
          setProgress({ phase: 'preparing', fraction: 0.1, message: 'Preparing photos…' });
        }
        const blobs = await Promise.all(
          added.map(async (photo) => {
            // Sized for the rank it lands on in the new roster, not the old one.
            const rank = photos.indexOf(photo) + 1;
            return toUploadBlob(renderBall(photo, exportSizeFor(rank, photos.length)));
          }),
        );

        await uploadAll(
          uploads.map((slot, index) => ({ slot, blob: blobs[index]! })),
          (done, total) =>
            setProgress({
              phase: 'uploading',
              fraction: 0.15 + 0.75 * (done / total),
              message: `Uploading ${done} of ${total}…`,
            }),
        );

        const newKeys = new Map(added.map((photo, index) => [photo.id, uploads[index]!.key]));
        const balls = photos.map((photo) => photo.savedKey ?? newKeys.get(photo.id)!);

        setProgress({ phase: 'finishing', fraction: 0.95, message: 'Saving the new lineup…' });
        const game = await saveEdit(slug, passphrase, balls);

        setProgress({ phase: 'done', fraction: 1 });
        return game;
      } catch (error) {
        setProgress({
          phase: 'error',
          fraction: 0,
          message: error instanceof Error ? error.message : 'Something went wrong saving.',
        });
        return null;
      }
    },
    [],
  );

  return { progress, save };
}
